import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";
import { displayCustomerName } from "@/lib/format";
import { useCustomerNavigation } from "./useCustomerNavigation";

// ============================================================================
// Right-aligned prev/next + position pill that sits in the breadcrumb row
// of CustomerDetailView. Self-hides when there's no list context (direct
// URL, board entry, etc.) so the breadcrumb stays clean.
//
// Linear / Gmail / GitHub-PR-review pattern:  [ ← ]  3 of 12  [ → ]
//
// Keyboard:  ←  prev   |   →  next
// Suppressed while an input/textarea/contenteditable holds focus, and
// while OS modifier keys are pressed (so Cmd+Left = browser-back still
// works as expected).
// ============================================================================

interface Props {
  currentCustno: string;
}

export function CustomerNavigation({ currentCustno }: Props) {
  const navigate = useNavigate();
  const nav = useCustomerNavigation(currentCustno);

  // Stable strings for the effect deps — `nav` is recreated each render
  // even when values are unchanged (URLSearchParams is a new object).
  const prevCustno = nav.prev?.custno ?? null;
  const nextCustno = nav.next?.custno ?? null;
  const preserved = nav.preservedQueryString;

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      // Skip when the user is typing somewhere.
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable
      ) {
        return;
      }
      // Skip when a modifier key is in play (Cmd+Left = browser-back, etc.)
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;

      const suffix = preserved ? `?${preserved}` : "";
      if (e.key === "ArrowLeft" && prevCustno) {
        e.preventDefault();
        navigate(`/customers/${prevCustno}${suffix}`);
      } else if (e.key === "ArrowRight" && nextCustno) {
        e.preventDefault();
        navigate(`/customers/${nextCustno}${suffix}`);
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [prevCustno, nextCustno, preserved, navigate]);

  if (nav.isLoading) return null;
  if (!nav.position) return null;

  const suffix = preserved ? `?${preserved}` : "";

  return (
    <div className="inline-flex items-center gap-0.5 rounded-full border border-slate-200 bg-white px-1 py-0.5 text-xs shadow-sm">
      <NavButton
        direction="prev"
        target={nav.prev}
        suffix={suffix}
        navigate={navigate}
      />
      <span className="px-2 font-medium tabular-nums text-slate-600">
        {nav.position.current} of {nav.position.total}
      </span>
      <NavButton
        direction="next"
        target={nav.next}
        suffix={suffix}
        navigate={navigate}
      />
    </div>
  );
}

function NavButton({
  direction,
  target,
  suffix,
  navigate,
}: {
  direction: "prev" | "next";
  target: { custno: string; fullname: string | null } | null;
  suffix: string;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const Icon = direction === "prev" ? ChevronLeft : ChevronRight;
  const disabled = !target;
  const targetName = target
    ? displayCustomerName(null, target.fullname)
    : null;
  const hint = direction === "prev" ? "← Previous" : "Next →";
  const title = target ? `${hint}: ${targetName}` : `No ${direction} customer`;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        if (!target) return;
        navigate(`/customers/${target.custno}${suffix}`);
      }}
      title={title}
      aria-label={title}
      className={cn(
        "rounded-full p-2 transition-colors",
        disabled
          ? "cursor-not-allowed text-slate-300"
          : "text-slate-700 hover:bg-slate-100",
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
